// Turns the saved data into everything the screens show: each person's expected pay per month, what goes to savings,
// which paydays are waiting for a review, the savings balance now and projected, and the yearly IRS picture.
//
// How savings work:
//   Each person's monthly contribution = the money they can really put aside: take-home pay (plus any IRS refund
//   that month) − their personal spending money − their own fixed expenses − their share of the joint expenses.
//   Expense reimbursements that come with the salary are taken out (they only give back money already spent), and
//   the meal card only counts when the settings say so. On payday the expected contribution is added to savings and
//   a review appears. The review records what really happened; once confirmed the month is frozen.
//   Expenses that turn out higher or lower than budgeted are a separate line for the month, so they never change a
//   payday someone already confirmed.

import { r2, sum } from './money.js';
import { addMonths, monthRange, monthOf, yearOf, monthNum, payday as paydayOf, monthLabel, isMonth, lastDay } from './calendar.js';
import { payslip, withPlanDefaults } from './payslip.js';
import { taxParamsFor } from './tax/params.js';
import { jovemYear } from './tax/irsJovem.js';
import { annualSettlement } from './tax/annual.js';
import { versionAt, personActiveIn } from './state.js';
import { monthsFromImport } from './sheetImport.js';

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const has = (v) => typeof v === 'number' && Number.isFinite(v);
const items = (list) => sum((list || []).map((x) => num(x.amount)));

export function expenseAmountIn(e, month) {
  if (!e || e.archived) return 0;
  if (e.end && month > e.end) return 0;
  const v = versionAt(e.versions, month);
  if (!v) return 0;
  const amount = num(v.amount);
  if (e.every === 'year') return e.spread ? r2(amount / 12) : monthNum(month) === Number(e.dueMonth || 1) ? amount : 0;
  if (e.every === 'once') return e.onMonth === month ? amount : 0;
  return amount;
}

const holidayOpts = (p) => ({ carnival: !!p.holidays?.carnival, extra: p.holidays?.extra || [] });

// Every person's payslips for whole calendar years (the IRS Jovem cap and the yearly IRS need the full year)
export function computeSlips(state, fromYear, toYear) {
  const out = {};
  for (const p of state.people) {
    const slips = {};
    const years = {};
    for (let y = fromYear; y <= toYear; y++) {
      const { params, estimated, sourceYear } = taxParamsFor(y, state.taxYears);
      const jovem = jovemYear(p.irsJovem, p.birthDate, y, params.irsJovem?.percentages, params.irsJovem?.maxAge);
      let capUsed = 0;
      let payments = 0;
      const tot = { gross: 0, taxable: 0, ss: 0, irs: 0, netBank: 0, meal: 0, allowance: 0, months: 0, employee: false };
      for (let m = 1; m <= 12; m++) {
        const key = `${y}-${String(m).padStart(2, '0')}`;
        let plan = versionAt(p.plans, key);
        // A first job that starts after tracking began, without a first day: it starts that month
        if (plan && plan === p.plans[0] && plan.mode === 'employee' && !plan.startDate && plan.from > state.settings.startMonth) plan = { ...plan, startDate: `${plan.from}-01` };
        const inputs = state.months?.[key]?.people?.[p.id] || {};
        const slip = plan ? payslip(plan, key, inputs, {
          params, jovem, holidayOpts: holidayOpts(p), capUsed, paymentsBefore: payments, appliedAtSource: p.irsJovem?.atSource !== false,
        }) : payslip(null, key);
        if (slip.capUsed !== undefined) capUsed = slip.capUsed;
        if (slip.payments !== undefined) payments = slip.payments;
        slips[key] = { plan: plan ? withPlanDefaults(plan) : null, slip, jovem, estimatedTax: estimated, taxYear: sourceYear };
        tot.gross += slip.gross; tot.taxable += slip.taxable; tot.ss += slip.ss; tot.irs += slip.irs;
        tot.netBank += slip.netBank; tot.meal += slip.mealCard; tot.allowance += slip.allowance;
        if (slip.netBank || slip.mealCard) tot.months++;
        if (slip.mode === 'employee') tot.employee = true;
      }
      Object.keys(tot).forEach((k) => { if (typeof tot[k] === 'number' && k !== 'months') tot[k] = r2(tot[k]); });
      const settlement = tot.employee && tot.taxable > 0 ? annualSettlement({
        params, gross: tot.taxable, socialSecurity: tot.ss, jovemPct: jovem.eligible ? jovem.pct : 0,
        personalDeductions: num(p.personalDeductions ?? 250), withheld: tot.irs,
      }) : null;
      years[y] = { ...tot, jovem, settlement, estimatedTax: estimated, taxYear: sourceYear };
    }
    out[p.id] = { slips, years };
  }
  return out;
}

// Sheet values for a person: { 'YYYY-MM': { actualNet, actualMeal, extraIncome, extraSpending, note } }
function sheetIndex(sheets, person) {
  const sheet = sheets?.[person.id];
  const rows = sheet ? monthsFromImport(sheet, person) : null;
  const idx = {};
  for (const r of rows || []) if (r && isMonth(r.month)) idx[r.month] = r.values || {};
  return idx;
}

export function sharesFor(people, incomes, split) {
  const n = people.length;
  if (!n) return {};
  const equal = Object.fromEntries(people.map((p) => [p.id, 1 / n]));
  const mode = split?.mode || 'income';
  if (mode === 'equal') return equal;
  if (mode === 'custom') {
    const raw = people.map((p) => Math.max(0, num(split.custom?.[p.id])));
    const total = raw.reduce((a, b) => a + b, 0);
    return total > 0 ? Object.fromEntries(people.map((p, i) => [p.id, raw[i] / total])) : equal;
  }
  const raw = people.map((p) => Math.max(0, num(incomes[p.id])));
  const total = raw.reduce((a, b) => a + b, 0);
  return total > 0 ? Object.fromEntries(people.map((p, i) => [p.id, raw[i] / total])) : equal;
}

// Has the person's first salary started by this month? (no salary yet, or a first job that starts later: no)
export function earningBy(p, key, startMonth) {
  const plan = versionAt(p.plans, key);
  if (!plan) return false;
  if (plan === p.plans[0] && plan.mode === 'employee') {
    const first = plan.startDate || (plan.from > startMonth ? `${plan.from}-01` : null);
    if (first && first > lastDay(key)) return false;
  }
  return true;
}

export function buildModel(state, { today, sheets = {} } = {}) {
  const settings = state.settings;
  const current = monthOf(today);
  const start = settings.startMonth;
  const lastStored = Object.keys(state.months || {}).filter(isMonth).sort().pop() || start;
  const lastTx = (state.savings?.transactions || []).map((t) => monthOf(t.date)).sort().pop() || start;
  const end = [addMonths(current, Math.max(1, Math.min(120, num(settings.horizon) || 24))), lastStored, lastTx, start].sort().pop();
  const keys = monthRange(start, end);
  const planFroms = state.people.flatMap((p) => (p.plans || []).map((x) => x.from));
  const fromYear = Math.min(yearOf(start), ...planFroms.map(yearOf));
  const computed = computeSlips(state, fromYear, yearOf(end) + 1);
  const refundMonth = Math.min(12, Math.max(1, Number(settings.irsRefund?.month) || 5));

  let balanceNow = num(state.savings?.opening);
  let projected = num(state.savings?.opening);
  const txByMonth = {};
  for (const t of state.savings?.transactions || []) {
    const k = monthOf(t.date);
    (txByMonth[k] = txByMonth[k] || []).push(t);
    if (k < start) { projected += num(t.amount); if (t.date <= today) balanceNow += num(t.amount); }
  }

  const sheetIdx = Object.fromEntries(state.people.map((p) => [p.id, sheetIndex(sheets, p)]));
  const months = [];
  const pending = [];
  let nextPayday = null;

  for (const key of keys) {
    const y = yearOf(key);
    const stored = state.months?.[key] || {};
    // Someone takes part from their first salary on (unless something was already typed for them that month)
    const people = state.people.filter((p) => personActiveIn(p, key, start) && (earningBy(p, key, start) || stored.people?.[p.id]));

    // Expenses
    const expenses = (state.expenses || []).map((e) => {
      const budget = expenseAmountIn(e, key);
      const override = stored.expenses && has(stored.expenses[e.id]) ? stored.expenses[e.id] : null;
      return { id: e.id, name: e.name, category: e.category, owner: e.owner || 'joint', budget, actual: override ?? budget, override };
    }).filter((e) => e.budget !== 0 || e.override !== null);
    const jointBudget = sum(expenses.filter((e) => e.owner === 'joint' || !people.some((p) => p.id === e.owner)).map((e) => e.budget));
    const variance = sum(expenses.map((e) => e.actual - e.budget));

    // Expected income first, to split joint expenses by income
    const base = {};
    for (const p of people) {
      const c = computed[p.id].slips[key];
      const slip = c.slip;
      const prevYear = computed[p.id].years[y - 1];
      const refund = settings.irsRefund?.include !== false && monthNum(key) === refundMonth && prevYear?.settlement && Math.abs(prevYear.settlement.refund) >= 1
        ? prevYear.settlement.refund : 0;
      const available = r2(slip.netBank - (settings.countAllowance === false ? slip.allowance : 0) + (settings.countMealCard === false ? 0 : slip.mealCard));
      base[p.id] = { c, slip, refund, available };
    }
    const shares = sharesFor(people, Object.fromEntries(people.map((p) => [p.id, base[p.id].available])), settings.split);

    const sheetsByPerson = Object.fromEntries(people.map((p) => [p.id, sheetIdx[p.id][key]]));
    const entries = people.map((p) => {
      const { c, slip, refund, available } = base[p.id];
      const sheetRow = sheetsByPerson[p.id];
      const review = { ...(stored.people?.[p.id] || {}) };
      const fromSheet = {};
      if (sheetRow) {
        for (const f of ['actualNet', 'actualMeal', 'reimbursed']) if (has(sheetRow[f])) { review[f] = sheetRow[f]; fromSheet[f] = true; }
        for (const f of ['extraIncome', 'extraSpending']) {
          if (Array.isArray(sheetRow[f])) { review[f] = sheetRow[f]; fromSheet[f] = true; } else if (has(sheetRow[f])) { review[f] = [{ id: 'sheet', label: 'From spreadsheet', amount: sheetRow[f] }]; fromSheet[f] = true; }
        }
        if (sheetRow.note) { review.note = sheetRow.note; fromSheet.note = true; }
      }
      const pd = paydayOf(key, p.payday, holidayOpts(p));
      const due = today >= pd;
      const confirmedInApp = !!review.confirmed;
      const confirmed = confirmedInApp || !!fromSheet.actualNet;
      const status = confirmed ? 'confirmed' : due ? 'pending' : 'upcoming';
      if (!due && (!nextPayday || pd < nextPayday.date)) nextPayday = { date: pd, personId: p.id, month: key };

      const personal = num(versionAt(p.allowance, key)?.amount);
      const own = sum(expenses.filter((e) => e.owner === p.id).map((e) => e.budget));
      const jointShare = r2(jointBudget * (shares[p.id] ?? 0));
      const snap = confirmedInApp && review.snapshot ? review.snapshot : null;
      const expected = snap ? { ...snap } : {
        net: slip.netBank,
        meal: slip.mealCard,
        allowance: slip.allowance,
        refund,
        income: r2(available + refund),
        personal,
        own,
        jointShare,
        share: shares[p.id] ?? 0,
        spending: r2(personal + own + jointShare),
      };
      if (!snap) expected.contribution = r2(expected.income - expected.spending);

      // Reimbursements arrive on top of the pay: when only they are typed, the pay itself was as expected
      const reimbursed = has(review.reimbursed) ? Math.max(0, review.reimbursed) : 0;
      const aNet = has(review.actualNet) ? review.actualNet : r2(expected.net + reimbursed);
      const aMeal = has(review.actualMeal) ? review.actualMeal : expected.meal;
      const aRefund = has(review.refundActual) ? review.refundActual : expected.refund;
      const extraIncome = items(review.extraIncome);
      const extraSpending = items(review.extraSpending);
      const allowanceOut = settings.countAllowance === false ? num(expected.allowance) : 0;
      const mealIn = settings.countMealCard === false ? 0 : aMeal;
      const actualIncome = r2(aNet - reimbursed - allowanceOut + mealIn + aRefund + extraIncome);
      const actualSpending = r2(expected.spending + extraSpending);
      const actual = {
        net: aNet, meal: aMeal, refund: aRefund, extraIncome, extraSpending, reimbursed,
        income: actualIncome, spending: actualSpending, contribution: r2(actualIncome - actualSpending),
        entered: has(review.actualNet) || has(review.actualMeal) || extraIncome !== 0 || extraSpending !== 0 || has(review.refundActual) || reimbursed > 0,
      };
      const entry = {
        personId: p.id, name: p.name, color: p.color, month: key, payday: pd, due, status, confirmed,
        source: fromSheet.actualNet ? 'sheet' : confirmedInApp ? 'app' : null, fromSheet,
        plan: c.plan, slip, jovem: c.jovem, estimatedTax: c.estimatedTax, taxYear: c.taxYear,
        review, expected, actual, difference: r2(actual.contribution - expected.contribution),
      };
      if (status === 'pending') pending.push(entry);
      return entry;
    });

    // Savings for the month
    const expectedSavings = r2(sum(entries.map((e) => e.expected.contribution)) - 0);
    const dueSavings = r2(sum(entries.filter((e) => e.due || e.confirmed).map((e) => e.actual.contribution)));
    const countVariance = key <= current;
    const tx = txByMonth[key] || [];
    const txNow = sum(tx.filter((t) => t.date <= today).map((t) => num(t.amount)));
    const txAll = sum(tx.map((t) => num(t.amount)));
    balanceNow = r2(balanceNow + dueSavings - (countVariance ? variance : 0) + txNow);
    const projectedSavings = r2(sum(entries.map((e) => (e.due || e.confirmed ? e.actual.contribution : e.expected.contribution))) - variance);
    projected = r2(projected + projectedSavings + txAll);

    const statuses = entries.map((e) => e.status);
    const status = !entries.length ? 'empty'
      : statuses.every((x) => x === 'confirmed') ? 'confirmed'
        : statuses.includes('pending') ? 'pending'
          : statuses.every((x) => x === 'upcoming') ? 'upcoming' : 'partial';

    months.push({
      key, label: monthLabel(key), year: y, isPast: key < current, isCurrent: key === current, isFuture: key > current,
      people: entries, expenses, jointBudget, totalBudget: sum(expenses.map((e) => e.budget)), variance, shares,
      transactions: tx, transactionsTotal: txAll,
      expectedSavings, savings: projectedSavings, status, note: stored.note || '',
      balance: key <= current ? balanceNow : projected, projectedBalance: projected,
    });
  }

  pending.sort((a, b) => (a.payday < b.payday ? -1 : a.payday > b.payday ? 1 : a.month < b.month ? -1 : a.month > b.month ? 1 : 0));
  const byKey = Object.fromEntries(months.map((m) => [m.key, m]));
  return {
    today, current, start, end, months, byKey, pending, nextPayday,
    balance: balanceNow,
    years: Object.fromEntries(state.people.map((p) => [p.id, computed[p.id].years])),
    slips: Object.fromEntries(state.people.map((p) => [p.id, computed[p.id].slips])),
  };
}

export const entryFor = (model, month, personId) => model.byKey[month]?.people.find((e) => e.personId === personId) || null;

// What to freeze when a payday is confirmed
export function snapshotOf(entry) {
  const e = entry.expected;
  return {
    net: e.net, meal: e.meal, allowance: e.allowance, refund: e.refund, income: e.income, personal: e.personal, own: e.own,
    jointShare: e.jointShare, share: e.share, spending: e.spending, contribution: e.contribution,
  };
}
